import React, { useRef, useEffect, useState, useMemo } from 'react';

import { TextField, FormControl, FormHelperText } from '@material-ui/core';
import { Autocomplete as AutocompleteUI } from '@material-ui/lab';
import { useField } from '@unform/core';
import { isEmpty, find, isEqual, isFunction } from 'lodash';
import PropTypes from 'prop-types';
import qs from 'querystring';

import { Hidden } from '~/components/Form';
import { canURL } from '~/helpers';
import api from '~/services/api';

function Autocomplete({
  name,
  label,
  title,
  getValue,
  serialize,
  url,
  params,
  unique,
  hiddens,
  first,
  disabled,
  excluded,
  variant,
  onSerialize,
  ...rest
}) {
  const inputRef = useRef(null);
  const permission = isEqual(canURL(), { read: true });

  const {
    fieldName,
    defaultValue,
    registerField,
    error,
    clearError,
  } = useField(name);

  const [data, setData] = useState([]);
  const [value, setValue] = useState(null);
  const [fieldHidden, setFieldHidden] = useState(hiddens);
  const [loading, setLoading] = useState(false);
  const [oldParams, setOldParams] = useState({});

  // Render items field hidden
  const renderItemsHidden = useMemo(
    () =>
      fieldHidden.map((fh) => (
        <Hidden name={fh.name} key={fh.name} value={fh.value || ''} />
      )),
    [fieldHidden]
  );

  function handleChangeValue(newValue) {
    const defValue = { [label]: null, [unique]: null };

    inputRef.current.value = serialize(newValue ? newValue[unique] : newValue);

    setValue(newValue);
    getValue(serialize(newValue || defValue));

    // Check exists field hidden
    if (!isEmpty(fieldHidden) && newValue) {
      const newHiddens = hiddens.map((nh) => ({
        ...nh,
        value: newValue[nh.name],
      }));

      setFieldHidden(newHiddens);
    } else {
      setFieldHidden(hiddens);
    }
  }

  async function handleData() {
    try {
      await setLoading(true);

      const response = await api.get(url, {
        params: { ...params, pagesize: 99999, pesquisalista: true },
        paramsSerializer: (param) => qs.stringify(param),
      });

      let modItems;
      const { items } = response.data;

      if (!isEmpty(response.data)) {
        if (isFunction(onSerialize)) {
          modItems = [...first, ...onSerialize(response.data)].filter(
            (mod) => !excluded.includes(mod[unique])
          );
        } else {
          modItems = [...first, ...items].filter(
            (mod) => !excluded.includes(mod[unique])
          );
        }

        await setData(modItems);
        await setOldParams(params);

        if (defaultValue) {
          const findItem = find(items, (i) => i[unique] === defaultValue);

          setValue(findItem);
          getValue(serialize(findItem));
        }
      }
    } catch (err) {
      // console.tron.log(err);
    } finally {
      await setLoading(false);
    }
  }

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
    });
  }, [registerField, fieldName]);

  useEffect(() => {
    handleData();
  }, []);

  useEffect(() => {
    if (!isEqual(params, oldParams) && !loading && !isEmpty(oldParams)) {
      handleData();
    }
  }, [url, params]);

  useEffect(() => {
    inputRef.current.value = defaultValue;
  }, [defaultValue]);

  useEffect(() => {
    if (disabled) clearError();
  }, [disabled]);

  return (
    <FormControl style={{ width: '100%' }}>
      <AutocompleteUI
        getOptionLabel={(option) => option[label].toString()}
        {...rest}
        size="small"
        options={data}
        value={value}
        onChange={(e, newValue) => handleChangeValue(newValue)}
        disabled={permission || disabled}
        onFocus={clearError}
        renderInput={(props) => (
          <TextField
            ref={inputRef}
            error={!!error}
            label={title}
            variant={variant}
            fullWidth
            {...props}
            disabled={permission || disabled}
          />
        )}
      />

      {error && <FormHelperText error>{error}</FormHelperText>}
      {renderItemsHidden}
    </FormControl>
  );
}

Autocomplete.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  getValue: PropTypes.func,
  serialize: PropTypes.func,
  url: PropTypes.string.isRequired,
  params: PropTypes.objectOf(PropTypes.any),
  unique: PropTypes.string.isRequired,
  hiddens: PropTypes.arrayOf(PropTypes.any),
  first: PropTypes.arrayOf(PropTypes.any),
  disabled: PropTypes.bool,
  excluded: PropTypes.arrayOf(PropTypes.any),
  variant: PropTypes.string,
  onSerialize: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
};

Autocomplete.defaultProps = {
  getValue: () => {},
  serialize: (value) => value,
  params: {},
  hiddens: [],
  first: [],
  disabled: false,
  excluded: [],
  variant: 'outlined',
  onSerialize: false,
};

export default Autocomplete;
